import { useState } from "react";
import { ChevronDown, ChevronRight, History, Sparkles } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

/**
 * Read-only summary of the most recent COMPLETED session for this client.
 * Displayed at the top of NewSession when continuity exists. Collapsible.
 */
export default function PreviousVisitPanel({ previous }) {
  const [open, setOpen] = useState(true);
  if (!previous || !previous.id) return null;
  const ai = previous.ai_analysis || {};
  const wrap = previous.wrap_up || {};
  const out = previous.outcome || {};

  return (
    <div className="bg-paper-50 border border-paper-200 rounded-3xl mb-8 overflow-hidden" data-testid="previous-visit-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 sm:px-6 py-4 hover:bg-paper-100/40 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-sage-500/10 flex items-center justify-center">
            <History className="w-4 h-4 text-sage-700" strokeWidth={1.75} />
          </div>
          <div className="text-left">
            <div className="label-overline text-sage-700">Previous visit</div>
            <div className="font-heading text-base text-ink-900">{fmtDate(previous.created_at)}</div>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-ink-500" /> : <ChevronRight className="w-4 h-4 text-ink-500" />}
      </button>
      {open && (
        <div className="px-5 sm:px-6 pb-5 sm:pb-6 space-y-4 text-sm">
          {previous.intake_notes && (
            <Row label="Chief complaint">{previous.intake_notes}</Row>
          )}
          {ai.summary && <Row label="Findings">{ai.summary}</Row>}
          {(previous.worked_areas || []).length > 0 && (
            <Row label="Worked">{previous.worked_areas.map((m) => m.replace(/_/g, " ")).join(", ")}</Row>
          )}
          {(wrap.home_care || []).length > 0 && (
            <Row label="Home care given">
              <ul className="space-y-1 mt-1">
                {wrap.home_care.map((h, i) => (
                  <li key={`hc-${i}-${h.slice(0, 16)}`} className="text-ink-700">• {h}</li>
                ))}
              </ul>
            </Row>
          )}
          {(out.pain_before != null || out.pain_after != null) && (
            <Row label="Pain last visit">
              <span className="text-ink-900">{out.pain_before ?? "?"} → {out.pain_after ?? "?"}</span>
              {out.client_feedback && <span className="text-ink-500"> · "{out.client_feedback}"</span>}
            </Row>
          )}
          <div className="pt-2 text-xs text-ink-500 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> AI uses this to compare today's findings against the last visit.
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="label-overline mb-1">{label}</div>
      <div className="text-ink-900 leading-relaxed">{children}</div>
    </div>
  );
}
